import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import * as THREE from 'three';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader';
import { PLYLoader } from 'three/examples/jsm/loaders/PLYLoader';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader';
// 10-15 대표 "360도 회전" → 자유 회전(TrackballControls). OrbitControls 는 위아래가 꼭대기·바닥에서 멈춰 뒤집어 볼 수 없었다
import { TrackballControls } from 'three/examples/jsm/controls/TrackballControls';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader';
import { unzipSync } from 'fflate';
import { X, RotateCcw, Loader } from 'lucide-react';
import { PREVIEW_LAYERS, DEFAULT_LAYER_VISIBILITY, previewLayerKey, isFocusLayerKey, loadRankOf, countLayers } from '../utils/previewLayers';

// Draco 디코더 — CDN 금지. three/examples/jsm/libs/draco/ 3파일을 public/draco/에 복사해 둔 것을 쓴다(2026-09-04 디자인 미리보기).
const DRACO_DECODER_PATH = '/draco/';
let sharedDracoLoader = null;
const getGltfLoader = () => {
    if (!sharedDracoLoader) {
        sharedDracoLoader = new DRACOLoader();
        sharedDracoLoader.setDecoderPath(DRACO_DECODER_PATH);
    }
    const loader = new GLTFLoader();
    loader.setDRACOLoader(sharedDracoLoader);
    return loader;
};

const KIND_LABEL = { crown: '크라운', bridge: '브릿지', inlay: '인레이', onlay: '온레이', veneer: '비니어', abutment: '어버트먼트', coping: '코핑', implant: '임플란트', denture: '덴처', splint: '스플린트', model: '모델', scan: '스캔' };
const kindLabelOf = (k) => (k ? (KIND_LABEL[String(k).toLowerCase()] || k) : '');
// 2026-10-15 대표 지시 "3D 미리보기에서 스캔 상악·하악도 보이게, 상악·하악·크라운·커스텀 어버트먼트를 각각 껐다 켰다"
//  — 09-29의 스캔 층 제외(트래픽 절감)를 대체. 스캔도 내려받아 한 장면에 그리고, 층별 토글(utils/previewLayers.js)로 숨김·보이기.
//  모델·다이는 2026-10-01 확정대로 기본 켬 유지.
const layerKeyOf = previewLayerKey;
// 층별 재질 — 스캔=불투명 회색 계열(상·하악 톤 차이 — 10-15 대표 "유리처럼 비치지 않게") / 크라운=치아색 / 어버트먼트=금속 / 모델=석고색 / 다이=조금 어둡게
// 2026-10-28 대표 "기존 것도 exocad 화면과 같은 스타일로": exocad 웹뷰 배색 — 보라 배경(#473a6d), 스캔 살구(#ffcd8c 계열), 보철 상아색, 병합 부품 청록.
const LAYER_MATERIAL = {
    crown: () => new THREE.MeshStandardMaterial({ color: 0xf2ebd8, metalness: 0.0, roughness: 0.5 }),
    abutment: () => new THREE.MeshStandardMaterial({ color: 0xb4bcc8, metalness: 0.75, roughness: 0.28 }),
    upper: () => new THREE.MeshStandardMaterial({ color: 0xe8c79a, metalness: 0.0, roughness: 0.8 }),
    lower: () => new THREE.MeshStandardMaterial({ color: 0xd8b584, metalness: 0.0, roughness: 0.8 }),
    scanbody: () => new THREE.MeshStandardMaterial({ color: 0x8fb3d9, metalness: 0.1, roughness: 0.6 }),
    scan: () => new THREE.MeshStandardMaterial({ color: 0xe0bf92, metalness: 0.0, roughness: 0.8 }),
    model: () => new THREE.MeshStandardMaterial({ color: 0xd9d4c7, metalness: 0.0, roughness: 0.9 }),
    die: () => new THREE.MeshStandardMaterial({ color: 0xbfb9ab, metalness: 0.0, roughness: 0.85 }),
};
// 토글 버튼의 색 점(범례) — 재질 색과 같은 값
const LAYER_SWATCH = { crown: '#f2ebd8', abutment: '#b4bcc8', upper: '#e8c79a', lower: '#d8b584', scanbody: '#8fb3d9', scan: '#e0bf92', model: '#d9d4c7', die: '#bfb9ab' };

const fmtSize = (n) => {
    if (!n && n !== 0) return '';
    if (n < 1024) return `${n}B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)}KB`;
    return `${(n / 1024 / 1024).toFixed(1)}MB`;
};

/**
 * 3D 미리보기 모달 (STL / PLY / OBJ / GLB·glTF(Draco) / zip)
 * - url이 .stl → 바로 로드
 * - url이 .zip (에이전트 case.zip) → 브라우저에서 해제 후 내부 STL 선택
 * - entries 가 있으면(디자인 미리보기 designPreview.files) 파일마다 탭(치식) — 각 탭 선택 시 그 url을 내려받아 format에 맞게 표시
 *
 * props: { url, fileName, onClose, entries?: [{ url, name, tooth, format:'glb-draco'|'stl', size, reduced }], title?, notice? }
 */
function StlViewer({ url, fileName, onClose, entries: entriesProp, title, notice }) {
    // 2026-10-15: 스캔 층도 표시 대상(종전 09-29 스캔 제외 필터 삭제) — 이하 코드는 전부 이 배열만 본다
    // 2026-10-28 대표 "HTML 자료가 있으면 그걸로": 에이전트가 올린 exocad 장면 HTML(format 'html')은 3D 층이 아니라 격리 창(iframe)으로 띄운다.
    //   있으면 기본 화면 = exocad 화면, [3D 뷰어] 탭으로 우리 뷰어 전환. 3D 로직은 html 항목을 뺀 배열만 본다.
    const htmlEntry = useMemo(() => (Array.isArray(entriesProp) ? entriesProp.find(e => e && String(e.format || '').toLowerCase() === 'html' && e.url) : null) || null, [entriesProp]);
    const entries = useMemo(() => (Array.isArray(entriesProp) ? entriesProp.filter(e => !(e && String(e.format || '').toLowerCase() === 'html')) : entriesProp), [entriesProp]);
    const [showExo, setShowExo] = useState(() => !!htmlEntry);
    const mountRef = useRef(null);
    const sceneRef = useRef(null); // { renderer, scene, camera, controls, mesh, frameId }
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [progress, setProgress] = useState(0);
    const [zipEntries, setZipEntries] = useState(null);   // [{ name, data }]
    const [activeEntry, setActiveEntry] = useState(null);
    const [imageView, setImageView] = useState(null);     // 이미지 항목 선택 시 blob URL
    const [activeRemote, setActiveRemote] = useState(0);  // entries 모드에서 선택된 파일 index

    const remoteMode = Array.isArray(entries) && entries.length > 0;
    // 배열 보기 (2026-09-07 대표 지시): 에이전트가 layer(crown|abutment|scan)를 붙여 올린 파일들은 한 장면에 케이스 좌표 그대로 겹쳐
    // 놓는다(파일별 중심 이동 없음 — exocad 산출물·스캔은 같은 좌표계, 실측 3케이스 확인). 회전 중심 = 크라운 중심 고정.
    const layoutMode = remoteMode && entries.some(e => e && e.layer);
    const [layerVis, setLayerVis] = useState(DEFAULT_LAYER_VISIBILITY);
    const layerVisRef = useRef(DEFAULT_LAYER_VISIBILITY);   // 나중에 도착한 층도 현재 토글 상태를 따르게
    const [layoutLoaded, setLayoutLoaded] = useState(0);
    // 층별 불러오기 상태 { key: { total, ok, fail, pct } } — pct는 지금 내려받는 파일의 %(없으면 null)
    const [layerStat, setLayerStat] = useState({});
    const [bgLoading, setBgLoading] = useState(false);       // 보철을 먼저 보여 준 뒤 스캔·모델을 이어 받는 중
    const layoutObjsRef = useRef([]);   // [{ layer, obj, entryIdx }]
    const layerCounts = useMemo(() => (layoutMode ? countLayers(entries) : {}), [layoutMode, entries]);
    const isZip = !remoteMode && /\.zip($|\?)/i.test(fileName || url || '');

    // ── three.js 장면 구성 ──
    const setupScene = useCallback(() => {
        const mount = mountRef.current;
        if (!mount) return null;

        const width = mount.clientWidth;
        const height = mount.clientHeight;

        const scene = new THREE.Scene();
        scene.background = new THREE.Color(0x473a6d);   // exocad 웹뷰 보라 배경(10-28)

        const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 5000);
        const renderer = new THREE.WebGLRenderer({ antialias: true });
        renderer.setSize(width, height);
        renderer.setPixelRatio(window.devicePixelRatio);
        mount.appendChild(renderer.domElement);

        scene.add(new THREE.HemisphereLight(0xffffff, 0x334155, 1.2));
        const dirLight = new THREE.DirectionalLight(0xffffff, 1.5);
        dirLight.position.set(1, 1, 2);
        scene.add(dirLight);
        const dirLight2 = new THREE.DirectionalLight(0xffffff, 0.6);
        dirLight2.position.set(-1, -0.5, -1);
        scene.add(dirLight2);

        const controls = new TrackballControls(camera, renderer.domElement);
        controls.rotateSpeed = 3.0;
        controls.zoomSpeed = 1.2;
        controls.dynamicDampingFactor = 0.15;
        // 2026-09-07 대표 지시: 크라운은 화면 중심에서만 움직인다 — 이동(pan)을 끄고 회전 중심을 파일 중심(0,0,0)에 고정
        controls.noPan = true;
        controls.target.set(0, 0, 0);

        const ctx = { renderer, scene, camera, controls, mesh: null, frameId: null };
        const animate = () => {
            ctx.frameId = requestAnimationFrame(animate);
            controls.update();
            renderer.render(scene, camera);
        };
        animate();
        sceneRef.current = ctx;
        return ctx;
    }, []);

    const disposeMesh = (ctx) => {
        if (ctx?.mesh) {
            ctx.scene.remove(ctx.mesh);
            ctx.mesh.traverse?.((child) => {
                if (child.isMesh) {
                    child.geometry?.dispose();
                    child.material?.dispose();
                }
            });
            if (ctx.mesh.isMesh) {
                ctx.mesh.geometry?.dispose();
                ctx.mesh.material?.dispose();
            }
            ctx.mesh = null;
        }
    };

    // geometry → 장면 배치 + 카메라 프레이밍
    const showGeometry = useCallback((geometry) => {
        const ctx = sceneRef.current || setupScene();
        if (!ctx) return;
        disposeMesh(ctx);

        geometry.computeVertexNormals();
        geometry.center();
        const material = new THREE.MeshStandardMaterial({
            color: 0xd6c9a8, metalness: 0.05, roughness: 0.55,
        });
        const mesh = new THREE.Mesh(geometry, material);
        // 스캔 좌표계 보정 (교합면이 위를 보도록)
        mesh.rotation.x = -Math.PI / 2;
        ctx.scene.add(mesh);
        ctx.mesh = mesh;

        geometry.computeBoundingSphere();
        const radius = geometry.boundingSphere?.radius || 30;
        ctx.radius = radius;
        ctx.camera.position.set(0, radius * 1.2, radius * 2.2);
        ctx.camera.lookAt(0, 0, 0);
        ctx.controls.target.set(0, 0, 0);
        ctx.controls.update();
    }, [setupScene]);

    // OBJ 등 그룹 객체 표시 (geometry가 아닌 Object3D)
    const showObject = useCallback((object) => {
        const ctx = sceneRef.current || setupScene();
        if (!ctx) return;
        disposeMesh(ctx);

        const material = new THREE.MeshStandardMaterial({ color: 0xd6c9a8, metalness: 0.05, roughness: 0.55 });
        object.traverse(child => {
            if (!child.isMesh) return;
            // GLB(Draco 축소본)에 normal이 없으면 조명 없이 검게 보인다 → 계산해서 채움 (검토 P1-1)
            if (child.geometry && !child.geometry.attributes?.normal) child.geometry.computeVertexNormals();
            child.material = material;
        });
        // 2026-09-07 대표 신고 "돌리면 크라운이 화면 전체로 움직인다" — 원인: 중심을 맞춘 뒤 90° 회전을 걸어
        // 회전 중심(0,0,0)과 크라운 중심이 어긋남(position은 회전 전 좌표, 실제 형상은 회전 후 좌표).
        // 회전을 먼저 적용하고 그 상태의 월드 중심을 빼야 크라운이 제자리에서 돈다.
        object.rotation.x = -Math.PI / 2;
        object.updateMatrixWorld(true);
        const box = new THREE.Box3().setFromObject(object);
        const center = box.getCenter(new THREE.Vector3());
        object.position.sub(center);
        object.updateMatrixWorld(true);
        ctx.scene.add(object);
        ctx.mesh = object;

        const radius = box.getSize(new THREE.Vector3()).length() / 2 || 30;
        ctx.radius = radius;
        ctx.camera.position.set(0, radius * 1.2, radius * 2.2);
        ctx.camera.lookAt(0, 0, 0);
        ctx.controls.target.set(0, 0, 0);
        ctx.controls.update();
    }, [setupScene]);

    /** 층 재질 입히기 + 현재 토글 상태 반영 (처음 배치·나중 추가 공용) */
    const applyLayerLook = (object, layer) => {
        const mat = (LAYER_MATERIAL[layer] || LAYER_MATERIAL.crown)();
        object.traverse(child => {
            if (!child.isMesh) return;
            if (child.geometry && !child.geometry.attributes?.normal) child.geometry.computeVertexNormals();
            child.material = mat;
        });
        object.userData.layer = layer;
        object.visible = layerVisRef.current[layer] !== false;
    };
    /** 배열 보기 — 전 파일을 한 그룹에 원좌표 그대로 배치. 크라운(없으면 전체) 중심을 원점으로 옮기고 그룹째 90° 회전 */
    const showLayout = useCallback((loaded) => {
        const ctx = sceneRef.current || setupScene();
        if (!ctx) return;
        disposeMesh(ctx);
        const root = new THREE.Group();
        const objs = [];
        loaded.forEach(({ layer, object, entryIdx }) => {
            applyLayerLook(object, layer);
            root.add(object);
            objs.push({ layer, obj: object, entryIdx });
        });
        root.rotation.x = -Math.PI / 2;
        root.updateMatrixWorld(true);
        // 회전 중심 = 크라운·어버트먼트 중심(월드) — 스캔·모델·다이는 제외(크라운 중심 유지), 없으면 전체 중심
        const focus = objs.filter(o => isFocusLayerKey(o.layer)).map(o => o.obj);
        const box = new THREE.Box3();
        (focus.length ? focus : objs.map(o => o.obj)).forEach(o => box.expandByObject(o));
        const center = box.getCenter(new THREE.Vector3());
        root.position.sub(center);
        root.updateMatrixWorld(true);
        ctx.scene.add(root);
        ctx.mesh = root;
        layoutObjsRef.current = objs;
        const radius = Math.max(box.getSize(new THREE.Vector3()).length() / 2, 8);
        ctx.radius = focus.length ? radius * 2.2 : radius * 1.1;   // 크라운 주변 악궁이 함께 보이도록 조금 멀리서 시작(크라운 없으면 전체가 들어오게)
        ctx.camera.position.set(0, ctx.radius * 1.2, ctx.radius * 2.2);
        ctx.camera.lookAt(0, 0, 0);
        ctx.controls.target.set(0, 0, 0);
        ctx.controls.update();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setupScene]);
    /** 배열 보기에 층 하나 추가 (보철을 먼저 띄운 뒤 도착한 스캔·모델) — 원좌표 그대로 root의 자식으로 넣으면 같은 변환을 받는다 */
    const addLayoutObject = ({ layer, object, entryIdx }) => {
        const ctx = sceneRef.current;
        if (!ctx?.mesh) return;
        applyLayerLook(object, layer);
        ctx.mesh.add(object);
        ctx.mesh.updateMatrixWorld(true);
        layoutObjsRef.current = [...layoutObjsRef.current, { layer, obj: object, entryIdx }];
    };
    /** 배열 보기 탭 클릭 — 그 크라운 중심으로 회전축 이동 */
    const focusLayoutEntry = (entryIdx) => {
        const ctx = sceneRef.current;
        const hit = layoutObjsRef.current.find(o => o.entryIdx === entryIdx);
        if (!ctx || !hit) return;
        const box = new THREE.Box3().setFromObject(hit.obj);
        const c = box.getCenter(new THREE.Vector3());
        const r = Math.max(box.getSize(new THREE.Vector3()).length() / 2, 5);
        ctx.controls.target.copy(c);
        const dir = ctx.camera.position.clone().sub(ctx.controls.target).normalize();
        ctx.camera.position.copy(c.clone().add(dir.multiplyScalar(r * 4)));
        ctx.controls.update();
    };
    const toggleLayer = (layer) => {
        const next = { ...layerVisRef.current, [layer]: !layerVisRef.current[layer] };
        layerVisRef.current = next;
        layoutObjsRef.current.forEach(o => { if (o.layer === layer) o.obj.visible = next[layer]; });
        setLayerVis(next);
    };
    const parseGlb = (arrayBuffer) => new Promise((resolve, reject) => {
        getGltfLoader().parse(arrayBuffer, '', (gltf) => resolve(gltf.scene), (e) => reject(e));
    });
    /** 배열 보기 파일 1개 → Object3D. 에이전트는 glb-draco만 올리지만 format 'stl'이면 STL로 읽는다(안전망) */
    const parseLayoutFile = async (arrayBuffer, en) => {
        const ext = String(en?.path || en?.name || '').split('?')[0].split('.').pop().toLowerCase();
        if (en?.format === 'stl' || (!en?.format && ext === 'stl')) {
            const geo = new STLLoader().parse(arrayBuffer);
            geo.computeVertexNormals();
            return new THREE.Mesh(geo);
        }
        return parseGlb(arrayBuffer);
    };

    // 확장자별 로드: STL/PLY(geometry), OBJ(group), GLB/glTF(Draco 포함, group), 이미지(오버레이)
    //  format 'glb-draco'(디자인 미리보기 축소본)이면 확장자와 무관하게 glTF 경로로 읽는다
    const loadEntryBuffer = useCallback((arrayBuffer, name, format) => {
        try {
            setError(null);
            const rawExt = (name || '').split('?')[0].split('.').pop().toLowerCase();
            const ext = (format === 'glb-draco' || format === 'glb' || format === 'gltf') ? 'glb' : rawExt;
            if (ext === 'glb' || ext === 'gltf') {
                getGltfLoader().parse(arrayBuffer, '', (gltf) => {
                    try {
                        showObject(gltf.scene);
                        setLoading(false);
                    } catch (e) {
                        console.error('GLB 장면 표시 실패:', e);
                        setError('3D 파일을 표시할 수 없습니다.');
                        setLoading(false);
                    }
                }, (e) => {
                    console.error('GLB 파싱 실패(Draco 디코더 /draco/ 확인):', e);
                    setError('3D 파일을 읽을 수 없습니다. 파일이 손상됐거나 디코더를 불러오지 못했습니다.');
                    setLoading(false);
                });
                return;
            }
            if (/^(png|jpg|jpeg)$/.test(ext)) {
                const blobUrl = URL.createObjectURL(new Blob([arrayBuffer], { type: `image/${ext === 'jpg' ? 'jpeg' : ext}` }));
                setImageView(prev => { if (prev) URL.revokeObjectURL(prev); return blobUrl; });
                setLoading(false);
                return;
            }
            setImageView(prev => { if (prev) URL.revokeObjectURL(prev); return null; });
            if (ext === 'ply') {
                showGeometry(new PLYLoader().parse(arrayBuffer));
            } else if (ext === 'obj') {
                const text = new TextDecoder().decode(arrayBuffer);
                showObject(new OBJLoader().parse(text));
            } else {
                showGeometry(new STLLoader().parse(arrayBuffer));
            }
            setLoading(false);
        } catch (e) {
            console.error('파일 파싱 실패:', e);
            setError('파일을 읽을 수 없습니다.');
            setLoading(false);
        }
    }, [showGeometry, showObject]);

    // URL → Uint8Array (진행률 표시). 실패는 예외로 올려 호출부가 문구를 띄운다
    //  onProgress(pct)를 주면 전체 진행 대신 그 콜백으로 알린다(배열 보기 층별 %)
    const fetchBuffer = useCallback(async (src, isCancelled, onProgress) => {
        const report = onProgress || setProgress;
        report(0);
        let lastPct = 0;
        const res = await fetch(src);
        if (!res.ok) throw new Error(`다운로드 실패 (${res.status})`);
        const total = Number(res.headers.get('content-length')) || 0;
        const reader = res.body.getReader();
        const chunks = [];
        let received = 0;
        for (;;) {
            const { done, value } = await reader.read();
            if (done) break;
            if (isCancelled?.()) return null;
            chunks.push(value);
            received += value.length;
            if (total) {
                const pct = Math.round(received / total * 100);
                if (pct !== lastPct) { lastPct = pct; report(pct); }
            }
        }
        const buffer = new Uint8Array(received);
        let offset = 0;
        for (const chunk of chunks) { buffer.set(chunk, offset); offset += chunk.length; }
        return buffer;
    }, []);

    // ── 파일 로드 ──
    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                setLoading(true);
                if (layoutMode) {
                    // 배열 보기: 보철(크라운·어버트먼트)을 먼저 받아 바로 보여 주고, 스캔바디→상·하악 스캔→모델 순으로 이어 받아 같은 장면에 더한다.
                    //  파일마다 따로 실패 처리 — 한 층이 실패해도 나머지는 그린다. 실패한 층은 토글에 「불러오지 못함」(조용한 실패 금지)
                    const init = {};
                    Object.entries(countLayers(entries)).forEach(([k, n]) => { init[k] = { total: n, ok: 0, fail: 0, pct: null }; });
                    setLayerStat(init);
                    setLayoutLoaded(0);
                    setBgLoading(false);
                    const bump = (key, fn) => setLayerStat(prev => {
                        const cur = prev[key] || { total: 0, ok: 0, fail: 0, pct: null };
                        return { ...prev, [key]: { ...cur, ...fn(cur) } };
                    });
                    const loadOne = async ({ en, i }) => {
                        const key = layerKeyOf(en);
                        if (!en?.url) {
                            console.error(`3D 미리보기 파일 주소 없음(층 ${key}, ${i + 1}번째)`);
                            bump(key, c => ({ fail: c.fail + 1, pct: null }));
                            return null;
                        }
                        try {
                            bump(key, () => ({ pct: 0 }));
                            const buf = await fetchBuffer(en.url, () => cancelled, (p) => { if (!cancelled) bump(key, () => ({ pct: p })); });
                            if (!buf || cancelled) return null;
                            const object = await parseLayoutFile(buf.buffer, en);
                            bump(key, c => ({ ok: c.ok + 1, pct: null }));
                            return { layer: key, object, entryIdx: i };
                        } catch (e) {
                            console.error(`3D 미리보기 층 불러오기 실패(층 ${key}, ${i + 1}번째):`, e);
                            if (!cancelled) bump(key, c => ({ fail: c.fail + 1, pct: null }));
                            return null;
                        }
                    };
                    const indexed = entries.map((en, i) => ({ en, i })).filter(x => x.en);
                    const first = indexed.filter(x => isFocusLayerKey(layerKeyOf(x.en)));
                    const rest = indexed.filter(x => !isFocusLayerKey(layerKeyOf(x.en)))
                        .sort((a, b) => loadRankOf(layerKeyOf(a.en)) - loadRankOf(layerKeyOf(b.en)));
                    let done = 0;
                    const loaded = [];
                    for (const x of first) {
                        const r = await loadOne(x);
                        if (cancelled) return;
                        if (r) loaded.push(r);
                        done += 1; setLayoutLoaded(done);
                    }
                    let shown = false;
                    if (loaded.length) { showLayout(loaded); shown = true; setLoading(false); }
                    const later = [];
                    if (rest.length && shown) setBgLoading(true);
                    for (const x of rest) {
                        const r = await loadOne(x);
                        if (cancelled) return;
                        done += 1; setLayoutLoaded(done);
                        if (!r) continue;
                        if (shown) addLayoutObject(r); else later.push(r);
                    }
                    setBgLoading(false);
                    if (!shown) {
                        if (!later.length) throw new Error('3D 파일을 하나도 불러오지 못했습니다. 잠시 후 다시 열어 보세요.');
                        showLayout(later);
                        setLoading(false);
                    }
                    return;
                }
                if (remoteMode) {
                    // 디자인 미리보기: 첫 파일부터. 탭 전환은 selectRemote
                    const first = entries[0];
                    if (!first?.url) throw new Error('파일 주소가 없습니다.');
                    const buf = await fetchBuffer(first.url, () => cancelled);
                    if (!buf || cancelled) return;
                    setActiveRemote(0);
                    loadEntryBuffer(buf.buffer, first.name || first.path || first.url, first.format);
                    return;
                }
                const buffer = await fetchBuffer(url, () => cancelled);
                if (!buffer || cancelled) return;

                if (isZip) {
                    const files = unzipSync(buffer);
                    // 3D(STL/PLY/OBJ) + 이미지(PNG/JPG) 모두 표시, 3D 파일 우선 정렬
                    const entries = Object.entries(files)
                        .filter(([name]) => /\.(stl|ply|obj|png|jpe?g)$/i.test(name))
                        .map(([name, data]) => ({ name, data }))
                        .sort((a, b) => {
                            const is3d = n => /\.(stl|ply|obj)$/i.test(n) ? 0 : 1;
                            return is3d(a.name) - is3d(b.name);
                        });
                    if (entries.length === 0) throw new Error('zip 안에 표시할 수 있는 파일이 없습니다.');
                    if (cancelled) return;
                    setZipEntries(entries);
                    setActiveEntry(entries[0].name);
                    loadEntryBuffer(entries[0].data.buffer.slice(entries[0].data.byteOffset, entries[0].data.byteOffset + entries[0].data.byteLength), entries[0].name);
                } else {
                    loadEntryBuffer(buffer.buffer, fileName || url);
                }
            } catch (e) {
                console.error('3D 미리보기 로드 실패:', e);
                if (!cancelled) { setError(e.message || '파일을 불러오지 못했습니다.'); setLoading(false); }
            }
        };
        load();
        return () => {
            cancelled = true;
            const ctx = sceneRef.current;
            if (ctx) {
                cancelAnimationFrame(ctx.frameId);
                disposeMesh(ctx);
                ctx.controls.dispose();
                ctx.renderer.dispose();
                ctx.renderer.domElement.remove();
                sceneRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [url, entries]);

    // entries 모드 탭 전환 — 파일마다 새로 내려받는다(치식별 파일이 따로 올라옴)
    const selectRemote = async (idx) => {
        const en = entries?.[idx];
        if (!en || idx === activeRemote) return;
        setActiveRemote(idx);
        setLoading(true);
        setError(null);
        try {
            const buf = await fetchBuffer(en.url);
            if (!buf) return;
            loadEntryBuffer(buf.buffer, en.name || en.path || en.url, en.format);
        } catch (e) {
            console.error('디자인 미리보기 파일 로드 실패:', e);
            setError(e.message || '파일을 불러오지 못했습니다.');
            setLoading(false);
        }
    };

    const selectEntry = (name) => {
        const entry = zipEntries?.find(e => e.name === name);
        if (!entry) return;
        setActiveEntry(name);
        setLoading(true);
        // 렌더 블로킹 방지
        setTimeout(() => {
            loadEntryBuffer(entry.data.buffer.slice(entry.data.byteOffset, entry.data.byteOffset + entry.data.byteLength), name);
        }, 30);
    };

    const resetView = () => {
        const ctx = sceneRef.current;
        if (!ctx?.mesh) return;
        const radius = ctx.radius || ctx.mesh.geometry?.boundingSphere?.radius || 30;
        ctx.camera.position.set(0, radius * 1.2, radius * 2.2);
        ctx.controls.target.set(0, 0, 0);
        ctx.controls.update();
    };

    const entryLabel = (name) => {
        const base = name.toLowerCase();
        if (/\.(png|jpe?g)$/.test(base)) return '사진';
        if (base.includes('upper')) return '상악';
        if (base.includes('lower')) return '하악';
        if (base.includes('bite') || base.includes('occlusion')) return '교합';
        return name.split('/').pop();
    };

    return (
        <div style={styles.overlay} onClick={onClose}>
            <div style={styles.modal} onClick={e => e.stopPropagation()}>
                <div style={styles.header}>
                    <span style={styles.title}>{title || fileName || '3D 미리보기'}</span>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <button style={styles.iconBtn} onClick={resetView} title="시점 초기화"><RotateCcw size={16} /></button>
                        <button style={styles.iconBtn} onClick={onClose} title="닫기"><X size={16} /></button>
                    </div>
                </div>

                {layoutMode && (
                    <div style={styles.layerBar}>
                        {/* 층별 보이기/숨기기 (2026-10-15 대표 지시: 상악·하악·크라운·커스텀 어버트먼트 + 있으면 스캔바디·모델·다이).
                            그 케이스에 없는 층은 버튼을 그리지 않는다. 받는 중이면 %, 실패하면 「불러오지 못함」 */}
                        {PREVIEW_LAYERS.map(({ key: k, label }) => {
                            const total = layerCounts[k] || 0;
                            if (!total) return null;
                            const st = layerStat[k] || { total, ok: 0, fail: 0, pct: null };
                            const settled = st.ok + st.fail >= total;
                            const allFailed = settled && st.ok === 0 && st.fail > 0;
                            const on = !!layerVis[k] && !allFailed;
                            let status = '';
                            if (allFailed) status = '불러오지 못함';
                            else if (st.pct != null) status = `${st.pct}%`;
                            else if (!settled) status = '대기';
                            else if (st.fail > 0) status = `${st.fail}개 못 불러옴`;
                            return (
                                <button key={k} type="button" disabled={allFailed}
                                    title={allFailed ? `${label} 파일을 불러오지 못했습니다. 창을 닫고 다시 열어 보세요.` : `${label} ${on ? '숨기기' : '보이기'}`}
                                    aria-pressed={on}
                                    style={{ ...styles.layerBtn, ...(on ? styles.layerBtnOn : {}), ...(allFailed ? styles.layerBtnFail : {}) }}
                                    onClick={() => toggleLayer(k)}>
                                    <span style={{ ...styles.swatch, background: LAYER_SWATCH[k] || '#cbd5e1', opacity: on ? 1 : 0.35 }} />
                                    <span>{on ? '☑' : '☐'}</span>
                                    <span>{label}</span>
                                    {status && <span style={allFailed || (settled && st.fail > 0) ? styles.layerStatusFail : styles.layerStatus}>{status}</span>}
                                </button>
                            );
                        })}
                        <span style={styles.layerHint}>
                            {(loading || bgLoading) ? `불러오는 중 ${layoutLoaded}/${entries.length}` : '케이스 배열 그대로 · 크라운 중심 회전 · 버튼으로 층 보이기/숨기기'}
                        </span>
                    </div>
                )}
                {htmlEntry && (
                    <div className="stl-tabs" style={{ ...styles.tabs, marginTop: 8 }}>
                        <button style={{ ...styles.tab, ...(showExo ? styles.tabActive : {}) }} onClick={() => setShowExo(true)} title="exocad가 저장할 때 내보낸 장면(악궁·대합·디자인)을 exocad 뷰어 그대로 봅니다">
                            <span style={styles.tabLabel}>exocad 화면</span>
                            <span style={styles.tabFile}>{[fmtSize(htmlEntry.size), '장면 전체'].filter(Boolean).join(' · ')}</span>
                        </button>
                        {remoteMode && (
                            <button style={{ ...styles.tab, ...(!showExo ? styles.tabActive : {}) }} onClick={() => setShowExo(false)} title="디자인 파일을 층별로 켜고 끄며 봅니다">
                                <span style={styles.tabLabel}>3D 뷰어</span>
                                <span style={styles.tabFile}>{`${entries.length}개 파일`}</span>
                            </button>
                        )}
                    </div>
                )}
                {remoteMode && !showExo && (
                    <div style={styles.scopeNote}>{layoutMode ? '디자인 완료된 보철물과 스캔(상·하악)·모델을 한 장면에 표시합니다' : '디자인 완료된 3D 파일을 표시합니다'}</div>
                )}
                {remoteMode && (layoutMode ? entries.filter(en => en && isFocusLayerKey(layerKeyOf(en))).length > 1 : entries.length > 1) && (
                    <div className="stl-tabs" style={styles.tabs}
                        onWheel={(e) => { e.currentTarget.scrollLeft += (e.deltaY + e.deltaX); }}>
                        {entries.map((en, idx) => {
                            if (layoutMode && !isFocusLayerKey(layerKeyOf(en))) return null;   // 스캔·모델·다이는 탭 없음(토글로만)
                            const base = (en.name || en.path || '').split('/').pop();
                            const label = [en.tooth ? `#${en.tooth}` : '', kindLabelOf(en.kind)].filter(Boolean).join(' ') || base || `파일 ${idx + 1}`;
                            return (
                                <button key={(en.path || en.url || '') + idx}
                                    style={{ ...styles.tab, ...(idx === activeRemote ? styles.tabActive : {}) }}
                                    onClick={() => { if (layoutMode) { setActiveRemote(idx); focusLayoutEntry(idx); } else selectRemote(idx); }} title={base}>
                                    <span style={styles.tabLabel}>{label}</span>
                                    <span style={styles.tabFile}>{[fmtSize(en.size), en.reduced ? '축소본' : ''].filter(Boolean).join(' · ') || base}</span>
                                </button>
                            );
                        })}
                    </div>
                )}

                {zipEntries && zipEntries.length > 1 && (
                    <div
                        className="stl-tabs"
                        style={styles.tabs}
                        onWheel={(e) => { e.currentTarget.scrollLeft += (e.deltaY + e.deltaX); }}
                    >
                        {zipEntries.map(e => {
                            const fileName = e.name.split('/').pop();
                            const label = entryLabel(e.name);
                            return (
                                <button
                                    key={e.name}
                                    style={{ ...styles.tab, ...(e.name === activeEntry ? styles.tabActive : {}) }}
                                    onClick={() => selectEntry(e.name)}
                                    title={fileName}
                                >
                                    <span style={styles.tabLabel}>{label === fileName ? '파일' : label}</span>
                                    <span style={styles.tabFile}>{fileName}</span>
                                </button>
                            );
                        })}
                    </div>
                )}

                <div ref={mountRef} style={styles.canvas}>
                    {htmlEntry && showExo && (
                        /* exocad 장면 HTML — 격리 창. allow-scripts 만(같은 출처·다운로드·팝업 없음). 파일은 자체 완결(외부 요청 없음), 10-28 헤드리스 확인 */
                        <iframe title="exocad 화면" src={htmlEntry.url} sandbox="allow-scripts" referrerPolicy="no-referrer"
                            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0, background: '#473a6d', zIndex: 3 }} />
                    )}
                    {imageView && (
                        <div style={styles.imageOverlay}>
                            <img src={imageView} alt="첨부 사진" style={styles.imageContent} />
                        </div>
                    )}
                    {loading && (
                        <div style={styles.centerMsg}>
                            <Loader size={22} style={{ animation: 'spin 1s linear infinite' }} />
                            <span style={{ marginTop: 8 }}>{layoutMode ? `불러오는 중 ${layoutLoaded}/${entries.length}` : (progress > 0 && progress < 100 ? `다운로드 중 ${progress}%` : '불러오는 중...')}</span>
                        </div>
                    )}
                    {error && <div style={{ ...styles.centerMsg, color: '#fca5a5' }}>{error}</div>}
                </div>
                <div style={styles.hint}>드래그: 회전 · 휠: 확대/축소 · 상단 ↺ 시점 초기화 (크라운은 화면 중심에 고정{layoutMode ? ' · 탭을 누르면 그 크라운으로 이동' : ''})</div>
                {notice && <div style={styles.notice}>{notice}</div>}
                <style>{`
                    @keyframes spin { to { transform: rotate(360deg); } }
                    .stl-tabs::-webkit-scrollbar { height: 8px; }
                    .stl-tabs::-webkit-scrollbar-track { background: #1e293b; border-radius: 4px; }
                    .stl-tabs::-webkit-scrollbar-thumb { background: #475569; border-radius: 4px; }
                    .stl-tabs::-webkit-scrollbar-thumb:hover { background: #64748b; }
                    .stl-tabs { scrollbar-width: thin; scrollbar-color: #475569 #1e293b; }
                `}</style>
            </div>
        </div>
    );
}

const styles = {
    overlay: {
        position: 'fixed', inset: 0, zIndex: 2000,
        backgroundColor: 'rgba(15, 23, 42, 0.75)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    },
    modal: {
        width: 'min(860px, 96vw)', backgroundColor: '#0f172a',
        borderRadius: 14, overflow: 'hidden', display: 'flex', flexDirection: 'column',
        boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
    },
    header: {
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 16px', borderBottom: '1px solid #1e293b',
    },
    title: { color: '#e2e8f0', fontSize: 14, fontWeight: 600, wordBreak: 'break-all' },
    iconBtn: {
        background: '#1e293b', border: 'none', color: '#94a3b8',
        borderRadius: 8, padding: 8, cursor: 'pointer', display: 'flex',
    },
    tabs: {
        display: 'flex', gap: 8, padding: '10px 16px 6px',
        flexWrap: 'nowrap', overflowX: 'auto', flexShrink: 0,
        WebkitOverflowScrolling: 'touch',
    },
    tab: {
        display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2,
        background: '#1e293b', color: '#94a3b8', border: 'none',
        borderRadius: 8, padding: '7px 12px', cursor: 'pointer',
        maxWidth: 200, textAlign: 'left', flexShrink: 0,
    },
    tabLabel: { fontSize: 13, fontWeight: 700 },
    tabFile: {
        fontSize: 11, opacity: 0.75, maxWidth: 176,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    },
    tabActive: { background: '#6366f1', color: '#fff' },
    // 휴대폰 폭: 버튼은 줄바꿈(wrap)으로 여러 줄, 버튼 안 글자는 한 줄 유지, 안내 문구는 별도 줄
    layerBar: { display: 'flex', gap: 6, alignItems: 'center', padding: '10px 12px 0', flexWrap: 'wrap' },
    layerBtn: {
        display: 'inline-flex', alignItems: 'center', gap: 5, minHeight: 32, whiteSpace: 'nowrap', maxWidth: '100%',
        background: '#1e293b', color: '#94a3b8', border: '1px solid #334155', borderRadius: 8, padding: '5px 10px',
        cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: 'inherit',
    },
    layerBtnOn: { background: '#0f766e', color: '#fff', borderColor: '#0f766e' },
    layerBtnFail: { background: '#1e293b', color: '#fca5a5', borderColor: '#7f1d1d', cursor: 'not-allowed' },
    swatch: { width: 10, height: 10, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.35)', flexShrink: 0 },
    layerStatus: { fontSize: 11, fontWeight: 600, opacity: 0.8 },
    layerStatusFail: { fontSize: 11, fontWeight: 700, color: '#fca5a5' },
    layerHint: { color: '#64748b', fontSize: 11, flexBasis: '100%', lineHeight: 1.4 },
    scopeNote: { padding: '8px 16px 0', color: '#94a3b8', fontSize: 11.5 },
    canvas: {
        position: 'relative', width: '100%', height: 'min(560px, 62vh)', marginTop: 10,
    },
    centerMsg: {
        position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: 13,
    },
    hint: { padding: '10px 16px', color: '#64748b', fontSize: 12, textAlign: 'center' },
    notice: { padding: '0 16px 12px', color: '#94a3b8', fontSize: 12, textAlign: 'center', lineHeight: 1.5 },
    imageOverlay: {
        position: 'absolute', inset: 0, zIndex: 2,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        backgroundColor: '#0f172a',
    },
    imageContent: { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' },
};

export default StlViewer;
